import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Snackbar,
  Alert,
  Box,
  Stack,
  Avatar,
  Chip,
  alpha,
} from '@mui/material';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from '../../Configs/axiosInstance';
import { MAIN_URL } from '../../Configs/Urls';
import LogoImage from '../../Assets/images/Logos/logo.JPG';
import { colorForName, initialsFor } from '../PatientsPage/Components/avatarUtils';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import MonitorWeightRoundedIcon from '@mui/icons-material/MonitorWeightRounded';

const defaultOrderedDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const categories = [
  "Early Morning",
  "Breakfast",
  "Mid Meal (after 2 hours)",
  "Lunch",
  "Evening",
  "Pre Dinner",
  "Dinner",
  "Post Dinner"
];

let setLocalStorageJSON = (key, data) => {
  localStorage.setItem(key, JSON.stringify(data));
};

function PatientDietView({ planId, onCloseForm, showPatientInfo, selectedWeekdefault, showDownload, weight }) {

  const [patientData, setPatietnData] = useState(null);
  const [DietPlan, setDietPlan] = useState(null);
  const [orderedCategories, setOrderedCategories] = useState([]);
  const [orderedDaysOfWeek, setOrderedDaysOfWeek] = useState(defaultOrderedDays);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const rotateDaysBasedOnStartDate = (startDateStr) => {
    try {
      const [day, month, year] = startDateStr.split('-');
      const startDate = new Date(`${year}-${month}-${day}`);
      const startIndex = startDate.getDay(); // 0 (Sun) - 6 (Sat)
      console.log("startIndex is : ", startIndex);

      const rotatedDays = [
        ...defaultOrderedDays.slice(startIndex),
        ...defaultOrderedDays.slice(0, startIndex)
      ];
      console.log("rotatedDays is : ", rotatedDays);

      setOrderedDaysOfWeek(rotatedDays);
    } catch (error) {
      console.error("Error parsing week start date:", error);
      setOrderedDaysOfWeek(defaultOrderedDays); // fallback
    }
  };

  let fetchPatientDietData = async () => {
    try {
      const response = await axios.get(`${MAIN_URL}patientdietplans/show/${planId}`);
      const { Patient, DietPlan, CategorisedCategories, weekDateStart } = response.data;

      setPatietnData(Patient);
      setDietPlan(DietPlan);
      setOrderedCategories(CategorisedCategories);

      // Adjust days based on start of week
      rotateDaysBasedOnStartDate(weekDateStart);

    } catch (error) {
      console.error("Error fetching data:", error);
      setSnackbarMessage("Error fetching Diet Plan data.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    if (planId) {
      fetchPatientDietData();
    }
  }, [planId]);

  const getRowColor = (index) => {
    return index % 2 === 0 ? '#f5f5f5' : '#e0f7fa';
  };

  const downloadPDF = () => {
    const doc = new jsPDF();

    // ===== LOGO (Top Right) =====
    const logoWidth = 35;
    const logoHeight = 15;
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.addImage(
      LogoImage,
      "PNG",
      pageWidth - logoWidth - 10, // right margin
      8,                           // top margin
      logoWidth,
      logoHeight
    );

    // ===== TITLE =====
    doc.setFontSize(18);
    doc.text("Patient Diet Plan", 14, 20);

    // ===== PATIENT INFO =====
    doc.setFontSize(12);
    doc.text(`Name: ${patientData?.name}`, 14, 30);
    doc.text(`Age: ${patientData?.age}`, 14, 35);

    const today = new Date();
    doc.text(`Date of Download: ${today.toLocaleDateString()}`, 14, 40);
    doc.text(`Week: ${selectedWeekdefault}`, 14, 45);

    // ===== TABLE =====
    const tableColumn = ["Day", "Category", "Items"];
    const tableRows = [];

    orderedDaysOfWeek.forEach((day) => {
      let isFirstCategoryForDay = true;

      orderedCategories.forEach((category) => {
        const items = DietPlan?.[day]?.[category]?.join(", ") || "---";

        if (isFirstCategoryForDay) {
          tableRows.push([day, category, items]);
          isFirstCategoryForDay = false;
        } else {
          tableRows.push(["", category, items]);
        }
      });
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 55,
      styles: { fontSize: 10 },
      didDrawCell: (data) => {
        if (
          data.column.index === 0 &&
          data.row.index !== 0 &&
          data.row.raw[0] === ""
        ) {
          doc.setDrawColor(255, 255, 255);
          doc.rect(
            data.cell.x,
            data.cell.y,
            data.cell.width,
            data.cell.height,
            "S"
          );
        }
      },
    });

    doc.save("diet-plan.pdf");
  };


  const consoleNonEmptyData = () => {
    let filteredPlan = {};
    Object.entries(DietPlan).forEach(([day, meals]) => {
      let nonEmptyMeals = Object.fromEntries(
        Object.entries(meals).filter(([_, items]) => !items.includes("---"))
      );
      if (Object.keys(nonEmptyMeals).length > 0) {
        filteredPlan[day] = nonEmptyMeals;
      }
    });
    setLocalStorageJSON("CopiedPlan", filteredPlan);
    setSnackbarMessage("Plan Copied Successfully");
    setSnackbarSeverity("success");
    setOpenSnackbar(true);
  };

  return (
    <Box>
      {showPatientInfo && (
        <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 2.5, borderRadius: 3, borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 2 }}>
            <Avatar sx={{ width: 56, height: 56, fontSize: '1.2rem', bgcolor: colorForName(patientData?.name) }}>
              {initialsFor(patientData?.name)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {patientData?.name}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                {patientData?.gender && <Chip label={patientData.gender} size="small" variant="outlined" />}
                <Typography variant="body2" color="text.secondary">{patientData?.age} yrs</Typography>
                <Typography variant="body2" color="text.secondary">·</Typography>
                <Typography variant="body2" color="text.secondary">{patientData?.contactNumber}</Typography>
                <Typography variant="body2" color="text.secondary">·</Typography>
                <Typography variant="body2" color="text.secondary">{patientData?.email}</Typography>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 2.5, borderRadius: 3, borderColor: 'divider' }}>
        <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', rowGap: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={(theme) => ({ bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main', width: 40, height: 40 })}>
              <CalendarMonthRoundedIcon />
            </Avatar>
            <Box>
              <Typography variant="caption" color="text.secondary">Week</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selectedWeekdefault}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={(theme) => ({ bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main', width: 40, height: 40 })}>
              <MonitorWeightRoundedIcon />
            </Avatar>
            <Box>
              <Typography variant="caption" color="text.secondary">Weight for this plan</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{weight || '—'}</Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Diet Plan</Typography>
        <Stack direction="row" spacing={1.5}>
          {showDownload &&
            <Button
              variant="contained"
              startIcon={<DownloadRoundedIcon />}
              onClick={downloadPDF}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Download as PDF
            </Button>}
          <Button
            variant="outlined"
            startIcon={<ContentCopyRoundedIcon />}
            onClick={consoleNonEmptyData}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Copy This Diet Plan
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, borderColor: 'divider' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
              <TableCell>Day</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Items</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderedDaysOfWeek?.map((day, dayIndex) =>
              categories.map((category, index) => (
                <TableRow key={`${day}-${category}`} style={{ backgroundColor: getRowColor(index) }}>
                  {index === 0 && (
                    <TableCell rowSpan={categories.length} sx={{ fontWeight: 700, borderRight: '1px solid', borderColor: 'divider' }}>
                      {day}
                    </TableCell>
                  )}
                  <TableCell>{category}</TableCell>
                  <TableCell>
                    {DietPlan?.[day]?.[category]?.join(', ') || '---'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PatientDietView;
